import { Button } from '@material-ui/core'
import Favorite from '@material-ui/icons/Favorite'
import React from 'react'
import { Link } from 'react-router-dom'
import { ImageCard } from '../../../../components/image-card'
import { useMarketplaceListingCardStyles } from './marketplace-listing-card.styles'

export type MarketplaceListingCardProps = {
  title: string
  priceText: string
  buyLabel: string
  /** Detail URL (image, title, and buy use the same route). */
  to: string
  /**
   * Marketplace image id — loaded via `ImageCard` + axios (auth). Prefer over `imageSrc`
   * when the API requires authenticated fetches.
   */
  imageId?: number | null
  imageSrc?: string | null
  /** Accessible name for the photo area when `imageSrc` is set. */
  imageAlt?: string
  /** Shown over the image bottom with heart icon; omit to hide the overlay. */
  beneficiaryCaption?: string | null
  /** Placeholder when there is no `imageSrc`. */
  noPhotoLabel?: string
  /** When there is no image, render this instead of `noPhotoLabel` (e.g. icon). */
  noPhotoSlot?: React.ReactNode
}

export const MarketplaceListingCard: React.FC<MarketplaceListingCardProps> = ({
  title,
  priceText,
  buyLabel,
  to,
  imageId,
  imageSrc,
  imageAlt,
  beneficiaryCaption,
  noPhotoLabel = '—',
  noPhotoSlot,
}) => {
  const classes = useMarketplaceListingCardStyles()
  const caption = beneficiaryCaption?.trim()
  const hasImageCard =
    imageId != null && Number.isFinite(imageId) && imageId > 0
  const src = !hasImageCard ? imageSrc?.trim() || null : null
  const noPhotoContent = noPhotoSlot ?? noPhotoLabel
  const hasVisualImage = hasImageCard || Boolean(src)

  const imageBlock = hasImageCard ? (
    <div className={classes.imageCardHolder}>
      <ImageCard imageId={Number(imageId)} />
      {caption ? (
        <div className={classes.overlay}>
          <div className={classes.captionRow}>
            <Favorite className={classes.heartIcon} aria-hidden />
            <span className={classes.captionText}>{caption}</span>
          </div>
        </div>
      ) : null}
    </div>
  ) : src ? (
    <div
      className={classes.imageFrame}
      style={{ backgroundImage: `url(${src})` }}
      role="img"
      aria-label={imageAlt ?? title}
    >
      {caption ? (
        <div className={classes.overlay}>
          <div className={classes.captionRow}>
            <Favorite className={classes.heartIcon} aria-hidden />
            <span className={classes.captionText}>{caption}</span>
          </div>
        </div>
      ) : null}
    </div>
  ) : (
    <div className={classes.noPhoto}>{noPhotoContent}</div>
  )

  return (
    <article className={classes.root}>
      <Link
        className={classes.imageLink}
        to={to}
        aria-label={
          hasVisualImage
            ? undefined
            : `${title}, ${
                typeof noPhotoContent === 'string' ? noPhotoContent : noPhotoLabel
              }`
        }
      >
        {imageBlock}
      </Link>
      <div className={classes.body}>
        <Link className={classes.titleLink} to={to}>
          <h3 className={classes.title}>{title}</h3>
        </Link>
        <p className={classes.price}>{priceText}</p>
        <div className={classes.buyButtonSlot}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            component={Link}
            to={to}
          >
            {buyLabel}
          </Button>
        </div>
      </div>
    </article>
  )
}
